import { useMemo, useState } from "react";
import { View, type LayoutChangeEvent } from "react-native";
import { LineChart } from "react-native-wagmi-charts";
import { useTranslation } from "react-i18next";

import type { Point } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { annotateChartSeries, resolveHeadlineIndex, type ChartSeries } from "./chartAnnotations";
import { buildChartA11ySummary } from "./chartA11ySummary";
import { ChartTapLayer, MARKER_EXTENT } from "./ChartTapLayer";
import { plotA11yProps, type PointSelection } from "./pointSelection";
import {
  buildWagmiChartModel,
  wagmiAxisRange,
  wagmiPixelMapper,
  WAGMI_BOTTOM_RESERVED,
} from "./wagmiChartModel";
import { WagmiChartA11yPoints } from "./WagmiChartA11yPoints";

// ⚠️ SPIKE PG-1 (16/09) — react-native-wagmi-charts messo accanto al nostro
// `LineChart`, SOLO sul glucosio, per rispondere alla domanda che il banco web
// non può rispondere: il tap prende il punto giusto **su Android**, dove
// `snapToPoint` è l'unica implementazione che conta?
//
// ⛔ Non è un'adozione. Se la prova boccia, questo file esce insieme a
// reanimated, worklets e alla riga di `babel.config.js` — cioè si ripristina
// `CN-41`, che aveva misurato **5,44 MB in meno** sull'APK.
//
// Cosa questa scena prova DAVVERO:
//  1. il tap con molti punti (il glucosio arriva al cap di 500);
//  2. che i token passino (⛔ wagmi ha `color = 'black'` di default: qui non
//     glielo lasciamo scegliere);
//  3. che il pan/scrub non litighi con lo ScrollView della schermata.
//
// PG-56 (25/09, dall'audit a11y di PG-34): questo grafico non esisteva per il
// lettore di schermo, zero prop a11y. Il fratello `WagmiLineChart.tsx` aveva
// già lo schema giusto: un riepilogo (`chartSummaryA11y`/`chartWindowA11y`,
// stesse chiavi i18n, nessuna nuova) sul contenitore `accessible` +
// `accessibilityRole="image"`, e un fratello non-visivo (`WagmiChartA11yPoints`)
// per i punti. Riusati qui, non ricopiati: `annotateChartSeries` +
// `wagmiAxisRange`/`buildWagmiChartModel` (glucosio come UNA serie sola,
// `key: "glucose"`), `resolveHeadlineIndex` (`chartAnnotations.ts`) per
// decidere chi porta il riepilogo, e le due `t()` che lo compongono, estratte
// in `chartA11ySummary.ts` (review 25/09: erano copiate a mano da
// `WagmiLineChart.tsx`, ora UNA funzione, chiamata da entrambi) calcolano lo
// stesso range/finestra che direbbe `WagmiLineChart` se montasse questa
// stessa serie. Il DISEGNO resta quello dello spike (la sua istanza
// `<LineChart.Provider>` propria, non l'albero di `WagmiPathLayers`):
// l'obiettivo era rendere questo file leggibile, non farlo confluire nel
// fratello.
//
// PG-64 fetta 3 (02/10): si legge al tocco, mai tenendo premuto, come
// ogni altra linea (`ChartTapLayer.tsx`): l'host passa una `selection` e la
// sua intestazione dice il punto scelto. Qui x è l'INDICE del punto (nessun
// `xDomain`) e wagmi usa il suo `yGutter` di default: la mappa del tocco
// riusa `wagmiPixelMapper` con quei due fatti, non una seconda formula.
const WAGMI_DEFAULT_Y_GUTTER = 16;

type Props = {
  points: Point[];
  theme: UiTheme;
  height?: number;
  /** Formatta un valore y per il riepilogo/i punti a11y (unit-aware, stesso
   *  ruolo del `yFormat` di `WagmiLineChart`). Il chiamante che conosce
   *  l'unità di glicemia scelta dall'utente (mg/dL o mmol/L) la passa qui;
   *  senza, il riepilogo dice solo il numero. */
  yFormat?: (y: number) => string;
  /** Stessa ragione del `nowMs` di `WagmiLineChart`: chi ha già un orologio
   *  (MetabolicCard) lo passa perché la finestra annunciata resti stabile
   *  tra un render e l'altro, non ri-derivata dall'ora di render. */
  nowMs?: number;
  /** Il tocco legge il grafico: indice nei `points` passati. Assente: il
   *  grafico non si tocca. */
  selection?: PointSelection;
};

export function WagmiSpikeChart({
  points,
  theme,
  height = 120,
  yFormat = (n) => n.toFixed(0),
  nowMs,
  selection,
}: Props) {
  const { t, i18n } = useTranslation();
  const data = points.map((p) => ({ timestamp: p.x, value: p.y }));
  // 🪤 wagmi NON conosce il suo contenitore: il default di `width` è
  // `Dimensions.get('window')`, cioè lo SCHERMO INTERO (`Chart.tsx:43,50`).
  // Dentro una card con i suoi margini, la linea esce dal bordo destro —
  // visto sul banco e confermato da Antenore sul telefono il 16/09.
  // ⛔ Non basta passare `width`: finché non l'abbiamo misurata il chart non
  // si monta affatto, altrimenti il primo render ricade sullo schermo pieno.
  const [boxWidth, setBoxWidth] = useState(0);
  const onLayout = (e: LayoutChangeEvent) => setBoxWidth(e.nativeEvent.layout.width);
  // Same margin as `WagmiLineChart`: the selection ring stays whole.
  const inset = MARKER_EXTENT;
  const width = Math.max(0, boxWidth - 2 * inset);

  // Stesso trattamento del fratello: una serie sola ("glucose"), la stessa
  // funzione pura di annotazione/asse, nessuna seconda copia di quella
  // matematica (`chartAnnotations.ts`/`wagmiChartModel.ts`).
  const now = nowMs ?? Date.now();
  const series = useMemo<ChartSeries[]>(() => [{ key: "glucose", points }], [points]);
  const built = useMemo(() => annotateChartSeries(series, now), [series, now]);
  const axis = useMemo(() => wagmiAxisRange(series, now), [series, now]);
  const { isEmpty, lo, hi, x0, xEnd } = useMemo(
    () => buildWagmiChartModel(series, built, axis),
    [series, built, axis],
  );

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }),
    [i18n.language],
  );
  // Stesso resolver del fratello (`chartAnnotations.ts`), non l'indice 0 a
  // mano: una sola serie è sempre l'unica candidata, ma la regola che decide
  // chi è "headline" resta UNA, condivisa, non due copie che potrebbero
  // divergere il giorno in cui questo file smette di essere mono-serie.
  const headlineIndex = resolveHeadlineIndex(built);
  const summaryLabel = buildChartA11ySummary({
    t,
    dateFmt,
    lo,
    hi,
    x0,
    xEnd,
    last: built[headlineIndex]?.last,
    yFormat,
  });

  if (isEmpty) return null;

  // `lo`/`hi` del modello sono il min/max che wagmi stesso usa (allargati di
  // 1 se coincidono: lì d3 mette la linea a metà, e così anche questa mappa).
  const { pxX, pxY } = wagmiPixelMapper(
    { x0: 0, xEnd: points.length - 1, lo, hi },
    width,
    height - WAGMI_BOTTOM_RESERVED,
    WAGMI_DEFAULT_Y_GUTTER,
  );
  const picked = selection ? points[selection.selected] : undefined;

  return (
    <View style={{ width: "100%" }}>
      <View
        style={{ height, marginTop: 8, overflow: "hidden" }}
        onLayout={onLayout}
        {...plotA11yProps(summaryLabel, selection)}
      >
        {width === 0 ? null : (
        <LineChart.Provider data={data}>
          <LineChart height={height} width={width} style={{ marginLeft: inset }}>
            <LineChart.Path color={theme.palette.brand500} width={2.5}>
              <LineChart.Dot color={theme.palette.brand500} at={data.length - 1} hasPulse />
            </LineChart.Path>
          </LineChart>
        </LineChart.Provider>
        )}
        {selection && width > 0 ? (
          <ChartTapLayer
            theme={theme}
            targets={points.map((_, i) => ({ i, x: pxX(i) }))}
            marker={picked ? { x: pxX(selection.selected), y: pxY(picked.y) } : undefined}
            selection={selection}
            width={width}
            height={height}
            left={inset}
          />
        ) : null}
      </View>

      {/* Fratello non-visivo del box `accessible` sopra, mai suo discendente:
          `accessible` collassa tutto sotto in UNA sola etichetta (il
          riepilogo), quindi i nodi per-punto stanno fuori, stesso schema di
          `WagmiLineChart.tsx` (`WagmiChartA11yPoints.tsx`, header comment). */}
      <WagmiChartA11yPoints built={built} yFormat={yFormat} />
    </View>
  );
}
